import { throttle } from 'lodash';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TableFiltersRef } from '../filters';

const SCROLL_THRESHOLD = 50;
const THROTTLE_DELAY = 200;

type UseStickyHeaderParams = {
    headerRef: React.RefObject<HTMLOwcTableHeaderElement>;
    scrollContainerRef?: React.RefObject<HTMLElement>;
    filterHeaderRef: React.RefObject<TableFiltersRef>;
    freezeVisibility?: boolean;
};

export const useStickyHeader = ({
    headerRef,
    scrollContainerRef,
    filterHeaderRef,
    freezeVisibility = false,
}: UseStickyHeaderParams) => {
    const [isVisibleFilter, setIsVisibleFilter] = useState<boolean>(true);
    const lastScrollTopRef = useRef(0);
    const isVisibleRef = useRef(true);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const observedFilterRef = useRef<HTMLElement | null>(null);

    // Sync --custom-table-sticky-top to the current filter height; a stale value leaves a gap
    // between the filter bar and the column header.
    const updateCssVariable = useCallback(() => {
        const headerEl = headerRef.current;
        const filterEl = filterHeaderRef.current?.element;
        const height = filterHeaderRef.current?.getHeight();
        if (headerEl && filterEl && height != null) {
            headerEl.style.setProperty('--custom-table-sticky-top', `${height}px`);
        }
    }, [headerRef, filterHeaderRef]);

    useLayoutEffect(() => {
        const resizeObserver = new ResizeObserver(updateCssVariable);
        resizeObserverRef.current = resizeObserver;

        return () => {
            resizeObserver.disconnect();
            resizeObserverRef.current = null;
            observedFilterRef.current = null;
        };
    }, [updateCssVariable]);

    // No deps: re-attach the observer to the current filter element every render, since the
    // filter re-mounts on navigation (key={location.key}) and a one-shot observer would keep
    // its stale initial height.
    useLayoutEffect(() => {
        updateCssVariable();

        const headerEl = headerRef.current;
        const filterEl = filterHeaderRef.current?.element ?? null;
        const resizeObserver = resizeObserverRef.current;

        if (!resizeObserver || !headerEl || filterEl === observedFilterRef.current) {
            return;
        }

        if (observedFilterRef.current) {
            resizeObserver.unobserve(observedFilterRef.current);
        }
        if (filterEl) {
            resizeObserver.observe(filterEl);
        }
        observedFilterRef.current = filterEl;
    });

    const handleScrollThrottled = useMemo(
        () =>
            throttle(
                (element: HTMLElement) => {
                    // Re-sync on scroll in case the ResizeObserver missed a filter change.
                    updateCssVariable();

                    if (freezeVisibility) {
                        if (!isVisibleRef.current) {
                            isVisibleRef.current = true;
                            setIsVisibleFilter(true);
                        }
                        lastScrollTopRef.current = element.scrollTop;
                        return;
                    }
                    const scrollTop = element.scrollTop;
                    const lastScrollTop = lastScrollTopRef.current;

                    if (Math.abs(scrollTop - lastScrollTop) < SCROLL_THRESHOLD) {
                        return;
                    }

                    const shouldBeVisible = scrollTop < lastScrollTop;

                    if (shouldBeVisible !== isVisibleRef.current) {
                        isVisibleRef.current = shouldBeVisible;
                        setIsVisibleFilter(shouldBeVisible);
                    }
                    lastScrollTopRef.current = scrollTop;
                },
                THROTTLE_DELAY,
                {
                    leading: false,
                    trailing: true,
                }
            ),
        [freezeVisibility, updateCssVariable]
    );

    useEffect(() => {
        const moduleScrollable = scrollContainerRef?.current;
        if (!moduleScrollable) {
            return;
        }

        const scrollHandler = (ev: Event) => handleScrollThrottled(ev.target as HTMLElement);
        moduleScrollable.addEventListener('scroll', scrollHandler);
        handleScrollThrottled(moduleScrollable); // Initial call

        return () => {
            moduleScrollable.removeEventListener('scroll', scrollHandler);
            handleScrollThrottled.cancel(); // Cancel pending throttled calls
        };
    }, [handleScrollThrottled, scrollContainerRef]);

    useEffect(() => {
        if (freezeVisibility && !isVisibleRef.current) {
            isVisibleRef.current = true;
            setIsVisibleFilter(true);
        }
    }, [freezeVisibility]);

    return { isVisibleFilter };
};
